import { useState, type CSSProperties } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Copy,
  Download,
  FileCode2,
  FormInput,
  GripVertical,
  ListFilter,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import type { IssueField, IssueFieldType, IssueForm, PresetId } from '../../domain/types';
import type { TranslationKey } from '../../i18n';
import { IconButton } from '../../components/IconButton';
import ui from '../../components/ui.module.css';
import styles from './EditorPanel.module.css';

const FIELD_DEFINITIONS: Array<{
  type: IssueFieldType;
  labelKey: TranslationKey;
  icon: typeof FormInput;
}> = [
  { type: 'markdown', labelKey: 'markdown', icon: FileCode2 },
  { type: 'input', labelKey: 'input', icon: FormInput },
  { type: 'textarea', labelKey: 'textarea', icon: AlignLeft },
  { type: 'dropdown', labelKey: 'dropdown', icon: ListFilter },
  { type: 'checkboxes', labelKey: 'checkboxes', icon: CheckSquare },
];

const PRESET_DEFINITIONS: Array<{ id: PresetId; key: TranslationKey; testId: string }> = [
  { id: 'bug-report', key: 'bugReport', testId: 'preset-bug' },
  { id: 'feature-request', key: 'featureRequest', testId: 'preset-feature' },
  { id: 'documentation', key: 'documentationIssue', testId: 'preset-docs' },
];

interface EditorPanelProps {
  form: IssueForm;
  selectedUid: string | null;
  activePreset: PresetId | null;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onPreset: (preset: PresetId) => void;
  onAdd: (type: IssueFieldType) => void;
  onSelect: (uid: string | null) => void;
  onPatchField: (uid: string, update: (field: IssueField) => IssueField) => void;
  onReorder: (fields: IssueField[]) => void;
  onMove: (uid: string, direction: -1 | 1) => void;
  onDuplicate: (uid: string) => void;
  onDelete: (uid: string) => void;
  onImport: () => void;
  onExport: () => void;
}

interface SortableFieldCardProps {
  field: IssueField;
  index: number;
  total: number;
  selected: boolean;
  t: EditorPanelProps['t'];
  onSelect: () => void;
  onPatch: (update: (field: IssueField) => IssueField) => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function fieldTitle(field: IssueField): string {
  if (field.type === 'markdown') {
    const firstLine = field.attributes.value.split('\n').find((line) => line.trim());
    return firstLine?.replace(/^#+\s*/, '').slice(0, 48) || 'Markdown';
  }
  return field.attributes.label || field.id || field.type;
}

function SortableFieldCard({
  field,
  index,
  total,
  selected,
  t,
  onSelect,
  onPatch,
  onMove,
  onDuplicate,
  onDelete,
}: SortableFieldCardProps) {
  const [expanded, setExpanded] = useState(true);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.uid,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as CSSProperties;
  const typeDefinition = FIELD_DEFINITIONS.find((item) => item.type === field.type);
  const TypeIcon = typeDefinition?.icon ?? CircleDot;

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`${styles.fieldCard} ${selected ? styles.selectedCard : ''} ${isDragging ? styles.dragging : ''}`}
      data-testid="field-card"
      data-field-uid={field.uid}
      onClick={onSelect}
    >
      <header className={styles.cardHeader}>
        <button
          type="button"
          className={styles.dragHandle}
          aria-label={t('drag')}
          title={t('drag')}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
        <TypeIcon size={15} strokeWidth={1.8} aria-hidden="true" />
        <button type="button" className={styles.cardTitle} onClick={() => setExpanded(!expanded)}>
          <span>{fieldTitle(field)}</span>
          <span className={styles.typeBadge}>{field.type}</span>
        </button>
        <IconButton
          icon={expanded ? ChevronDown : ChevronRight}
          label={expanded ? t('collapse') : t('expand')}
          onClick={() => setExpanded(!expanded)}
        />
      </header>

      {expanded ? (
        <div className={styles.quickEdit} onClick={(event) => event.stopPropagation()}>
          {field.type === 'markdown' ? (
            <label className={styles.compactField}>
              <span>{t('markdownValue')}</span>
              <textarea
                rows={3}
                value={field.attributes.value}
                data-testid="field-label"
                onFocus={onSelect}
                onChange={(event) =>
                  onPatch((current) =>
                    current.type === 'markdown'
                      ? { ...current, attributes: { value: event.target.value } }
                      : current,
                  )
                }
              />
            </label>
          ) : (
            <div className={styles.quickEditGrid}>
              <label className={styles.compactField}>
                <span>{t('label')}</span>
                <input
                  value={field.attributes.label}
                  data-testid="field-label"
                  onFocus={onSelect}
                  onChange={(event) => {
                    const value = event.target.value;
                    onPatch((current) =>
                      current.type === 'markdown'
                        ? current
                        : ({
                            ...current,
                            attributes: { ...current.attributes, label: value },
                          } as IssueField),
                    );
                  }}
                />
              </label>
              <label className={styles.compactField}>
                <span>{t('id')}</span>
                <input
                  value={field.id}
                  data-testid="field-id"
                  onFocus={onSelect}
                  onChange={(event) => {
                    const id = event.target.value;
                    onPatch((current) =>
                      current.type === 'markdown' ? current : ({ ...current, id } as IssueField),
                    );
                  }}
                />
              </label>
            </div>
          )}
        </div>
      ) : null}

      <footer className={styles.cardActions} onClick={(event) => event.stopPropagation()}>
        <IconButton
          icon={ArrowUp}
          label={t('moveUp')}
          data-testid="move-up"
          disabled={index === 0}
          onClick={() => onMove(-1)}
        />
        <IconButton
          icon={ArrowDown}
          label={t('moveDown')}
          data-testid="move-down"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
        />
        <IconButton icon={Copy} label={t('duplicate')} onClick={onDuplicate} />
        <IconButton icon={Trash2} label={t('delete')} onClick={onDelete} />
      </footer>
    </article>
  );
}

export function EditorPanel({
  form,
  selectedUid,
  activePreset,
  t,
  onPreset,
  onAdd,
  onSelect,
  onPatchField,
  onReorder,
  onMove,
  onDuplicate,
  onDelete,
  onImport,
  onExport,
}: EditorPanelProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = form.body.findIndex((field) => field.uid === active.id);
    const newIndex = form.body.findIndex((field) => field.uid === over.id);
    if (oldIndex >= 0 && newIndex >= 0) onReorder(arrayMove(form.body, oldIndex, newIndex));
  };

  return (
    <aside className={styles.panel} aria-label={t('builder')} data-testid="builder-panel">
      <section className={styles.section}>
        <h2>{t('presets')}</h2>
        <div className={styles.presetList}>
          {PRESET_DEFINITIONS.map((preset) => (
            <button
              type="button"
              className={styles.presetButton}
              aria-pressed={activePreset === preset.id}
              data-testid={preset.testId}
              key={preset.id}
              onClick={() => onPreset(preset.id)}
            >
              <FileCode2 size={15} strokeWidth={1.8} aria-hidden="true" />
              {t(preset.key)}
            </button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2>{t('fields')}</h2>
        <div className={styles.fieldLibrary}>
          {FIELD_DEFINITIONS.map(({ type, labelKey, icon: Icon }) => (
            <button
              type="button"
              className={styles.libraryButton}
              data-testid={`add-${type}`}
              key={type}
              onClick={() => onAdd(type)}
            >
              <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
              <span>{t(labelKey)}</span>
              <Plus size={14} strokeWidth={1.8} aria-hidden="true" />
            </button>
          ))}
        </div>
      </section>

      <section className={`${styles.section} ${styles.fieldListSection}`}>
        <div className={styles.sectionTitleRow}>
          <h2>{t('fieldList')}</h2>
          <span>{form.body.length}</span>
        </div>
        {form.body.length === 0 ? <p className={styles.empty}>{t('noFields')}</p> : null}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={form.body.map((field) => field.uid)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.fieldList}>
              {form.body.map((field, index) => (
                <SortableFieldCard
                  field={field}
                  index={index}
                  total={form.body.length}
                  selected={field.uid === selectedUid}
                  t={t}
                  key={field.uid}
                  onSelect={() => onSelect(field.uid)}
                  onPatch={(update) => onPatchField(field.uid, update)}
                  onMove={(direction) => onMove(field.uid, direction)}
                  onDuplicate={() => onDuplicate(field.uid)}
                  onDelete={() => onDelete(field.uid)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </section>

      <section className={styles.importExport}>
        <button type="button" className={ui.button} data-testid="import-open" onClick={onImport}>
          <Upload size={15} strokeWidth={1.8} aria-hidden="true" />
          {t('import')}
        </button>
        <button type="button" className={ui.button} onClick={onExport}>
          <Download size={15} strokeWidth={1.8} aria-hidden="true" />
          {t('export')}
        </button>
      </section>
      <p className={styles.privacy}>{t('privateNotice')}</p>
    </aside>
  );
}
